/**
 * Capo — a row of capo-fret buttons: a guitar-side view of the SAME concert
 * transpose a Transpose row drives, offset by the global drop tuning.
 *
 *   active fret       = observedTranspose - dropTuning   (= transpose + |drop|)
 *   press fret F       → concertTranspose = F + dropTuning  (then dispatch)
 *
 * Example (Eb guitar, drop -1): concert transpose 0 → capo +1 lights; press
 * capo +3 → concert transpose +2 is set + sent, and the Transpose row relights
 * to +2. Both rows light from the SAME authority, so they stay in lock-step
 * with no per-item state and no remount.
 *
 * ⚠️ THE LOCK-STEP CLAIM ABOVE WAS FALSIFIED FOR A DAY, and that is why this
 * note exists. It used to read *"both rows read the same session signal"* -
 * true until CP-145 repointed Transpose to the setlist readback and left this
 * row on `concertTranspose()`, a localStorage latch whose only writers are a
 * tap here, a tap on a Transpose row, and a Rig State recall. Nothing in
 * `setlist/` writes it (measured: zero matches), so a song change could not
 * move this row - which is exactly what the operator reported on 2026-09-21:
 * *"autotune and rec quant both update when opsetlist loads a song, but
 * transpose and capo are still not moving."* The repoint fixed one of the two
 * rows the invariant was about, and the invariant itself was the thing that
 * should have caught it.
 *
 * So: light from the authority, never from the latch - the same rule
 * `Transpose.tsx` and the metronome and OBS rows already follow. `null`
 * means NO cell lights; it must never fall back to fret 0, because an open
 * capo is a real position.
 *
 * Shares the Transpose row's visuals (`.transpose` / `.transpose-cell`), so
 * `data-pending` styling comes for free and needs no new CSS.
 */

import { pushToast } from '../ui/toast.ts';
import { transposeShown } from '../transpose/shown.ts';
import { For, type Accessor } from 'solid-js';
import { editOwnsGesture, isEditMode, type CapoItem, type LayoutColor } from '../layout/store.ts';
import { setConcertTranspose, capoForTranspose, transposeForCapo, lastTransposeSend } from '../transpose/session.ts';
import { setlistState, observedTranspose } from '../setlist/state.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { TrayTitle, trayAttrs } from './TrayTitle.tsx';

interface CapoProps {
  item: CapoItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

export function Capo(props: CapoProps) {
  // Show-mode guard (SD-5): first tap on a fret arms it (2s), second fires.
  const guard = createDiscreteGuard(() => !!props.item.guard);

  const cells = (): number[] => {
    const out: number[] = [];
    for (let f = props.item.start; f <= props.item.end; f++) out.push(f);
    return out;
  };

  /**
   * The capo fret for what Live ACTUALLY has, or null when we cannot say.
   *
   * May fall outside this row's range (e.g. a negative concert transpose an
   * open-start capo can't reach) — then no cell lights, which is the honest
   * signal. Same when the readback is null: ⚠️ NEVER fall back to fret 0,
   * because an open capo is a real position and is indistinguishable from
   * "we have not been told" the moment it is defaulted.
   */
  const activeFret = (): number | null => {
    const t = transposeShown();
    return t === null ? null : capoForTranspose(t);
  };

  /**
   * Our own pending press, shown until the readback agrees — Transpose's
   * convention, and it earns its place here for the same reason: the round
   * trip is us → OPBridge → Live → readback, so a press is not instantly
   * reflected and an unlit row would look like the press was lost.
   *
   * ⚠️ Reads `lastTransposeSend()`, NOT `concertTranspose()`. Deriving it
   * from the latch put a semi-highlight on a fret nobody pressed - operator,
   * 2026-09-21: *"capo 3 and transpose +2 seem to be semi highlighted"*, both
   * from ONE stale latch at +2 (capo = 2 - (-1) = 3). See the long note on
   * `lastTransposeSend`.
   *
   * Cleared by comparison, not by a timer: once `activeFret` equals what we
   * pressed, the press is no longer pending; once a readback NEWER than the
   * press disagrees, the press lost. A stuck pending therefore means the
   * readback genuinely never agreed, which is information.
   */
  const pendingFret = (): number | null => {
    const send = lastTransposeSend();
    if (!send) return null;                        // nothing sent: a hydrated latch is not a send
    const fret = capoForTranspose(send.value);
    if (activeFret() === fret) return null;        // the readback agreed
    const m = setlistState();
    if (m.status === 'ok' && m.appliedAt > send.at) return null;  // a song SENT after the press overwrote it (`appliedAt`: not the per-poll `at`, and not a held song the operator only scrolled to)
    return fret;
  };

  function onCellDown(fret: number, e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    if (!guard.gate(String(fret))) return; // guarded: first tap only armed
    // Map the fret through the drop tuning to the concert transpose, set +
    // dispatch to this row's own configured bridge(s) (multi-select,
    // 2026-08-13). This relights the Transpose row + every Capo row.
    void setConcertTranspose(transposeForCapo(fret), props.item.bridgeIds).then(sayIfNotSent);
  }
  /** A tap no computer took says so, once per few seconds (operator 2026-11-03, decision 6: with "newest wins" only a
   *  DELIVERED tap shows, so an undelivered one would otherwise look like nothing happened). */
  let lastNotSentAt = 0;
  function sayIfNotSent(out: { ok: boolean; detail?: string }): void {
    if (out.ok || Date.now() - lastNotSentAt < 3000) return;
    lastNotSentAt = Date.now();
    pushToast('error', `Capo: not sent - ${out.detail ?? 'no computer took it'}.`, 4000);
  }

  return (
    <div class="transpose capo" data-token={COLOR_TOKEN[props.item.color]} {...trayAttrs(props.item)}>
      <TrayTitle item={props.item} />
      <For each={cells()}>
        {(fret) => (
          <button
            type="button"
            class="transpose-cell"
            data-active={activeFret() === fret ? 'true' : 'false'}
            data-pending={pendingFret() === fret ? 'true' : 'false'}
            data-unknown={activeFret() === null ? 'true' : 'false'}
            data-armed={guard.armedKey() === String(fret) ? 'true' : 'false'}
            data-home={fret === 0 ? 'true' : 'false'}
            data-semitone={fret}
            onPointerDown={(e) => onCellDown(fret, e)}
            title={fret === 0 ? 'No capo (open)' : `Capo fret ${fret}`}
          >
            {String(fret)}
          </button>
        )}
      </For>
    </div>
  );
}
